import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LIST_STALE_TIME, OVERVIEW_STALE_TIME } from '../../lib/queryDefaults';
import { unwrap } from '../../data/api';
import {
  incomeAPI,
  recordsAPI,
  sourcesAPI,
} from '../../data/income';
import { invalidateFinancialData } from '../financialCache';

export const incomeKeys = {
  all: ['income'],
  sources: () => [...incomeKeys.all, 'sources'],
  records: (filters) => [...incomeKeys.all, 'records', filters || {}],
  monthlySummary: (params) => [
    ...incomeKeys.all,
    'monthly-summary',
    params || {},
  ],
  whatIf: (payload) => [...incomeKeys.all, 'what-if', payload || {}],
};


export function useIncomeSourcesQuery(queryOptions = {}) {
  return useQuery({
    queryKey: incomeKeys.sources(),
    queryFn: () => sourcesAPI.list().then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

export function useIncomeRecordsQuery(filters, queryOptions = {}) {
  const enabled =
    queryOptions.enabled !== false &&
    Boolean(
      filters?.received_on_from ||
        filters?.received_on_to ||
        (filters?.year && filters?.month),
    );
  return useQuery({
    queryKey: incomeKeys.records(filters),
    queryFn: () => recordsAPI.list(filters).then((r) => unwrap(r, [])),
    enabled,
    staleTime: LIST_STALE_TIME,
    placeholderData: (prev) => prev,
    ...queryOptions,
  });
}

export function useMonthlySummaryQuery(params, queryOptions = {}) {
  const enabled =
    queryOptions.enabled !== false &&
    Boolean(
      params?.period === 'selfie' ||
        (params?.received_on_from && params?.received_on_to) ||
        (params?.year && params?.month),
    );
  return useQuery({
    queryKey: incomeKeys.monthlySummary(params),
    queryFn: () => incomeAPI.getMonthlySummary(params).then((r) => unwrap(r)),
    enabled,
    staleTime: OVERVIEW_STALE_TIME,
    placeholderData: (prev) => prev,
    ...queryOptions,
  });
}

async function invalidateMonthly(qc) {
  await invalidateFinancialData(qc);
}

export function useCreateIncomeSourceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => sourcesAPI.create(payload).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateMonthly(qc);
    },
  });
}

export function useUpdateIncomeSourceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      sourcesAPI.update(id, payload).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateMonthly(qc);
    },
  });
}

export function useDeleteIncomeSourceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => sourcesAPI.remove(id).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateMonthly(qc);
    },
  });
}

export function useUpsertIncomeRecordsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      recordsAPI.bulkUpsert(payload).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateMonthly(qc);
    },
  });
}

export function useCreateIncomeRecordMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => recordsAPI.create(payload).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateMonthly(qc);
    },
  });
}

export function useUpdateIncomeRecordMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      recordsAPI.update(id, payload).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateMonthly(qc);
    },
  });
}

export function useDeleteIncomeRecordMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => recordsAPI.remove(id).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateMonthly(qc);
    },
  });
}

// CHANGE v2 §18 + §19: useAllocateMutation / useWhatIfMutation removidos
// (Rendimentos passa a ser página única; QuickIncomeSheet substitui o fluxo).
